/**
 * Geräte-Sync über einen Ordner — die Logik, ohne Anbieter.
 *
 * **Was überhaupt zu synchronisieren ist.** Die Tauchgänge kommen aus der
 * Pipeline und liegen als Bundle vor; die auf zwei Geräte zu spiegeln wäre
 * derselbe Weg zweimal. Was nur in der App entsteht, ist der Check-in — Brevets,
 * Versicherung, Notfallkontakt, Pass. Genau das wandert hier von Gerät zu Gerät.
 *
 * **Verschlüsselt bleibt verschlüsselt.** Der Abzug enthält die Check-in-Sätze
 * unverändert, also als Geheimtext samt Salz und Prüfpaket. Der Schlüssel
 * wandert nie mit; auf dem zweiten Gerät wird dasselbe Passwort eingegeben
 * (CLAUDE.md: niemals Klartext in den Cloud-Ordner).
 *
 * **Zusammenführen nach Zeitstempel.** Jeder Satz trägt `geaendert`; der jüngere
 * gewinnt. Gelöschtes hinterlässt einen Grabstein — ohne ihn käme ein gelöschter
 * Ausweis beim nächsten Abgleich vom anderen Gerät zurück.
 */

export const FORMAT = 'tauchlogbuch-sync/1';

/** Kennung dieses Geräts — bleibt lokal, dient nur der Nachvollziehbarkeit. */
export function geraeteKennung(speicher = globalThis.localStorage) {
  const schluessel = 'tauchlogbuch.geraet';
  let kennung = speicher?.getItem(schluessel);
  if (!kennung) {
    kennung = globalThis.crypto.randomUUID();
    speicher?.setItem(schluessel, kennung);
  }
  return kennung;
}

/** Der Abzug, wie er in den Ordner geschrieben wird. */
export function baueAbzug({ checkin = [], tresor = null, grabsteine = [], geraet }) {
  return {
    format: FORMAT,
    erzeugt: new Date().toISOString(),
    geraet,
    tresor,
    checkin,
    grabsteine,
  };
}

export function pruefeAbzug(abzug, bereich = 'checkin') {
  if (!abzug || typeof abzug !== 'object') throw new Error('Die Datei enthält keinen Abzug.');
  if (abzug.format !== FORMAT) {
    throw new Error(`Unbekanntes Format: ${abzug.format ?? 'keines'}. Erwartet wird ${FORMAT}.`);
  }
  if (!Array.isArray(abzug[bereich])) {
    throw new Error(bereich === 'checkin'
      ? 'Im Abzug fehlt der Check-in-Bereich.'
      : `Im Abzug fehlt der Bereich ${bereich}.`);
  }
  return abzug;
}

const zeit = (satz) => String(satz?.geaendert ?? '');

// Grabsteine tragen je nach Herkunft einen anderen Zeitstempel: der Check-in
// schreibt `geaendert`, der Schreibweg in `saetze.js` `geloescht_am`. Ohne
// diese Rueckfallebene waere die Zeit leer und ein geloeschter Tauchgang kaeme
// beim naechsten Abgleich zurueck.
const grabZeit = (g) => String(g?.geaendert ?? g?.geloescht_am ?? '');

/**
 * Zwei Stände zusammenführen.
 *
 * `bereiche` nennt, welche Listen zu vergleichen sind — `['checkin']` für die
 * Ausweise, `['dives', 'sites', 'trips', 'buddies', 'gear']` für das Logbuch.
 * Die Regel ist in beiden Fällen dieselbe: Je Satz gewinnt der jüngere
 * Zeitstempel, Grabsteine zuletzt.
 *
 * Zurück kommen die zusammengeführten Bereiche unter ihrem Namen, dazu
 * Grabsteine, Tresor und ein Bericht. Nichts wird stillschweigend
 * überschrieben: Jeder Fall steht im Bericht.
 */
export function fuehreZusammen(lokal, fern, bereiche = ['checkin']) {
  const bericht = { uebernommen: [], behalten: [], neu: [], geloescht: [], konflikte: [] };
  const grabsteine = new Map(
    [...(lokal.grabsteine ?? []), ...(fern.grabsteine ?? [])].map((g) => [g.id, g]),
  );
  const ergebnis = { grabsteine: [...grabsteine.values()] };

  for (const bereich of bereiche) {
    const nachId = new Map((lokal[bereich] ?? []).map((satz) => [satz.id, satz]));
    const zusammen = new Map(nachId);

    for (const fernsatz of fern[bereich] ?? []) {
      const eigener = nachId.get(fernsatz.id);
      if (!eigener) {
        zusammen.set(fernsatz.id, fernsatz);
        bericht.neu.push(fernsatz.id);
        continue;
      }
      if (zeit(fernsatz) > zeit(eigener)) {
        zusammen.set(fernsatz.id, fernsatz);
        bericht.uebernommen.push(fernsatz.id);
        bericht.konflikte.push({ id: fernsatz.id, gewinner: 'fern',
          lokal: zeit(eigener), fern: zeit(fernsatz) });
      } else if (zeit(fernsatz) < zeit(eigener)) {
        bericht.behalten.push(fernsatz.id);
        bericht.konflikte.push({ id: fernsatz.id, gewinner: 'lokal',
          lokal: zeit(eigener), fern: zeit(fernsatz) });
      }
    }

    // Grabsteine zuletzt: Ein gelöschter Satz darf nicht durch einen älteren
    // Abzug wieder auferstehen.
    for (const [id, grabstein] of grabsteine) {
      const satz = zusammen.get(id);
      if (satz && grabZeit(grabstein) >= zeit(satz)) {
        zusammen.delete(id);
        bericht.geloescht.push(id);
        // Was ankommt und sofort wieder verschwindet, ist nicht "neu" — sonst
        // meldet der Bericht mehr, als am Ende dasteht.
        bericht.neu = bericht.neu.filter((eintrag) => eintrag !== id);
        bericht.uebernommen = bericht.uebernommen.filter((eintrag) => eintrag !== id);
      }
    }

    ergebnis[bereich] = [...zusammen.values()]
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));
  }

  // Der Tresor gehört zum Passwort, nicht zum Gerät: Der erste gewinnt, damit
  // ein zweites Gerät nicht das Salz austauscht und alles unlesbar macht.
  ergebnis.tresor = lokal.tresor ?? fern.tresor ?? null;
  ergebnis.bericht = bericht;
  return ergebnis;
}

/** Kurzfassung für die Oberfläche. */
export function fasseZusammen(bericht) {
  const teile = [];
  if (bericht.neu.length) teile.push(`${bericht.neu.length} neu`);
  if (bericht.uebernommen.length) teile.push(`${bericht.uebernommen.length} aktualisiert`);
  if (bericht.behalten.length) teile.push(`${bericht.behalten.length} lokal behalten`);
  if (bericht.geloescht.length) teile.push(`${bericht.geloescht.length} gelöscht`);
  return teile.length ? teile.join(', ') : 'nichts zu tun — beide Stände sind gleich';
}
